'use client';

import { useRef, useState } from 'react';
import { Check, Loader2, Search, Swords } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import type { AvailableCompetitor } from '@/lib/apis';

interface CompetitorComboboxProps {
  /** Competitors available to target. Parent filters out ones that already have an ad. */
  options: AvailableCompetitor[];
  selectedId: string | null;
  onSelect: (competitor: AvailableCompetitor | null) => void;
  isBusy?: boolean;
  placeholder?: string;
}

/**
 * Command-based combobox for choosing which competitor a new ad targets. Follows the
 * LabelCombobox / LandingPageCombobox pattern (search input, popover list) rather than a plain
 * select: a campaign can link hundreds of competitors, so the list has to be searchable.
 */
export function CompetitorCombobox({
  options,
  selectedId,
  onSelect,
  isBusy = false,
  placeholder = 'Search competitors...',
}: CompetitorComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = query.trim();
  const filtered = trimmed
    ? options.filter(
        (o) =>
          o.competitor_brand_name.toLowerCase().includes(trimmed.toLowerCase()) ||
          (o.primary_website ?? '').toLowerCase().includes(trimmed.toLowerCase()),
      )
    : options;

  const selected = options.find((o) => o.id === selectedId) ?? null;

  const choose = (competitor: AvailableCompetitor) => {
    if (isBusy) return;
    onSelect(competitor);
    setQuery('');
    setOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <div
        className={cn(
          'flex items-center min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          isBusy && 'opacity-50',
        )}
        onClick={() => {
          inputRef.current?.focus();
          setOpen(true);
        }}
      >
        {isBusy ? (
          <Loader2 className="h-4 w-4 text-gray-400 animate-spin flex-shrink-0" />
        ) : (
          <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
        <input
          ref={inputRef}
          value={open ? query : selected?.competitor_brand_name ?? query}
          placeholder={placeholder}
          disabled={isBusy}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground ml-2 text-gray-900"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter' && filtered.length > 0) {
              e.preventDefault();
              choose(filtered[0]);
            }
          }}
        />
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg">
          <Command shouldFilter={false}>
            <CommandList className="max-h-64">
              {filtered.length === 0 && (
                <CommandEmpty className="py-4 text-center text-sm text-gray-500">
                  {trimmed ? 'No matching competitors.' : 'Every competitor on this campaign already has an ad.'}
                </CommandEmpty>
              )}

              {filtered.length > 0 && (
                <CommandGroup>
                  {filtered.map((competitor) => (
                    <CommandItem
                      key={competitor.id}
                      value={competitor.id}
                      onSelect={() => choose(competitor)}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
                        <Swords className="h-3.5 w-3.5 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 block truncate">
                          {competitor.competitor_brand_name}
                        </span>
                        {competitor.primary_website && (
                          <span className="text-sm text-gray-500 block truncate">{competitor.primary_website}</span>
                        )}
                      </div>
                      {competitor.id === selectedId && (
                        <Check className="h-4 w-4 text-[#290BD9] flex-shrink-0" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
